import React, { useEffect, useRef } from 'react';

import { Button } from '../../components/Button/Button';
import { PlusIcon } from '../../components/icons/Plus';
import { ProductCard } from './ProductCard';
import type { Athlete } from './athletes';
import { SPONSORS_MARQUEE_THRESHOLD } from './athletes';

type MobileAthleteFeatureProps = {
    athlete: Athlete;
    productsOpen: boolean;
    onToggleProducts: () => void;
};

export function MobileAthleteFeature({ athlete, productsOpen, onToggleProducts }: MobileAthleteFeatureProps) {
    const { firstName } = athlete;
    const sponsorsScrolling = athlete.sponsors.length > SPONSORS_MARQUEE_THRESHOLD;

    const videoRef = useRef<HTMLVideoElement>(null);
    const videoUrl = athlete.expandedVideo;

    // One persistent <video>, driven imperatively. Previously the element was
    // keyed on athlete.id with autoPlay, so every drawer selection destroyed
    // and recreated it — a fresh media session and a full re-fetch per swap,
    // which is the repetitive-request churn on mobile. Here the element is
    // reused: changing the src attribute triggers the browser's load algorithm,
    // and this effect plays the new clip once it can. The poster covers the
    // reload gap so there's no black frame between athletes.
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !videoUrl) { return; }
        video.play().catch(() => {
            // play() can reject (not enough data yet, autoplay policy, or a
            // rapid swap interrupting the previous load); ignore — the next
            // selection's effect will drive playback.
        });
    }, [videoUrl]);

    return (
        <div className={`athlete-carousel__mobile-card${productsOpen ? ' athlete-carousel__mobile-card--products-open' : ''}`}>
            {(athlete.mobileExpandedVideo || athlete.expandedVideo) && (
                <video
                    ref={videoRef}
                    className="athlete-carousel__mobile-image"
                    src={athlete.mobileExpandedVideo || athlete.expandedVideo}
                    poster={athlete.mobilePosterImage || athlete.posterImage || undefined}
                    loop
                    muted
                    playsInline
                    aria-hidden="true"
                    preload="auto"
                />
            )}
            <div className="athlete-carousel__mobile-overlay" />

            {athlete.sponsors.length > 0 && (
                <div
                    className={`athlete-carousel__mobile-logos${sponsorsScrolling ? ' athlete-carousel__mobile-logos--scrolling' : ''}`}
                    aria-label={`${athlete.name} sponsors`}
                >
                    <div className="athlete-carousel__mobile-logos-track">
                        {athlete.sponsors.map((logo, index) => (
                            <span className="athlete-carousel__mobile-logo" key={`${logo}-${index}`}>
                                <img src={logo} alt="" aria-hidden="true" loading="lazy" decoding="async" />
                            </span>
                        ))}
                        <div className="athlete-carousel__mobile-logos-clone" aria-hidden="true">
                            {athlete.sponsors.map((logo, index) => (
                                <span className="athlete-carousel__mobile-logo" key={`clone-${logo}-${index}`}>
                                    <img src={logo} alt="" aria-hidden="true" loading="lazy" decoding="async" />
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}


            <div className={`athlete-carousel__mobile-products-preview${productsOpen ? ' athlete-carousel__mobile-products-preview--open' : ''}`}>
                <button
                    type="button"
                    className="athlete-carousel__mobile-products-top"
                    onClick={onToggleProducts}
                    aria-expanded={productsOpen}
                    aria-label={productsOpen ? `Close ${firstName}'s faves` : `Open ${firstName}'s faves`}
                >
                    <span>{firstName}'s faves</span>
                    <PlusIcon className={productsOpen ? 'athlete-carousel__plus athlete-carousel__plus--close' : 'athlete-carousel__plus'} />
                </button>
                <div className="athlete-carousel__mobile-products-body" aria-hidden={!productsOpen}>
                    <div className="athlete-carousel__mobile-products-track">
                        {athlete.products.map((product) => (
                            <ProductCard key={product.id} product={product} />
                        ))}
                    </div>
                    <Button label={`Shop ${firstName}'s faves`} href={`/@${athlete.id}`} className="athlete-carousel__mobile-shop-btn" />
                </div>
            </div>
        </div>
    );
}
